import { useImperativeHandle, useRef, useState } from 'react';
const initialDragDropState = {
    isDragging: false,
    dragColumn: null,
    dropColumn: null,
    dragRect: null
};
/** Viewport `cellRect` → coordinates inside `table-head-row` for `position: absolute` + `overflow: hidden`. */
function dragRectRelativeToRow(cellRect, offsetX, rowEl) {
    if (rowEl === null) {
        return {
            top: cellRect.top,
            left: cellRect.left + offsetX,
            width: cellRect.width,
            height: cellRect.height
        };
    }
    const rowRect = rowEl.getBoundingClientRect();
    return {
        top: cellRect.top - rowRect.top,
        left: cellRect.left - rowRect.left + offsetX,
        width: cellRect.width,
        height: cellRect.height
    };
}
/** Drop target: column whose header cell contains the center of the drag ghost (viewport coords). */
function findDropColumnUnderGhostCenter(columns, headerCells, cellRect, offsetX) {
    const centerX = cellRect.left + offsetX + cellRect.width / 2;
    const centerY = cellRect.top + cellRect.height / 2;
    for (const col of columns) {
        const el = headerCells.get(col.id);
        if (el === undefined) {
            continue;
        }
        const r = el.getBoundingClientRect();
        if (centerX >= r.left && centerX <= r.right && centerY >= r.top && centerY <= r.bottom) {
            return col;
        }
    }
    return null;
}
export function useTableHeaderRow({ ref, columns, groupColumn, onSortingChanged, onOrderChanging, onOrderChanged, onWidthChanging, onWidthChanged }) {
    const headerRowRef = useRef(null);
    const headerCellElementsRef = useRef(new Map());
    const [dragDropState, setDragDropState] = useState(initialDragDropState);
    /** Mirrors dropColumn state for mouseup; avoids stale React state. */
    const dropColumnRef = useRef(null);
    const onColumnClick = (column) => {
        onSortingChanged(column);
    };
    const syncDropFromGhost = (cellRect, offsetX) => {
        return findDropColumnUnderGhostCenter(columns, headerCellElementsRef.current, cellRect, offsetX);
    };
    const onColumnRef = (column, columnElement) => {
        if (columnElement !== null) {
            headerCellElementsRef.current.set(column.id, columnElement);
        }
        else {
            headerCellElementsRef.current.delete(column.id);
        }
    };
    const onColumnDragStart = (column, offsetX) => {
        var _a;
        const cellRect = ref.current.getColumnElement(column.id).getBoundingClientRect();
        const dragRect = dragRectRelativeToRow(cellRect, offsetX, headerRowRef.current);
        const dropColumn = (_a = syncDropFromGhost(cellRect, offsetX)) !== null && _a !== void 0 ? _a : column;
        dropColumnRef.current = dropColumn;
        onOrderChanging(column, dropColumn);
        setDragDropState((prev) => (Object.assign(Object.assign({}, prev), { isDragging: true, dragColumn: column, dropColumn,
            dragRect })));
    };
    const onColumnDragMove = (column, offsetX) => {
        const cellRect = ref.current.getColumnElement(column.id).getBoundingClientRect();
        let dropColumn = syncDropFromGhost(cellRect, offsetX);
        if (dropColumn === groupColumn) {
            dropColumn = column;
        }
        dropColumnRef.current = dropColumn;
        setDragDropState((prev) => (Object.assign(Object.assign({}, prev), { dropColumn, dragRect: dragRectRelativeToRow(cellRect, offsetX, headerRowRef.current) })));
        onOrderChanging(column, dropColumn);
    };
    const onColumnDragEnd = (column, _offsetX) => {
        const dropColumn = dropColumnRef.current;
        dropColumnRef.current = null;
        onOrderChanged(column, dropColumn);
        setDragDropState(initialDragDropState);
    };
    const onColumnResize = (column, offsetX) => {
        onWidthChanging(column, columns[columns.indexOf(column) + 1], offsetX);
    };
    const onColumnResizeEnd = (column, offsetX) => {
        onWidthChanged(column, columns[columns.indexOf(column) + 1], offsetX);
    };
    useImperativeHandle(ref, () => ({
        getColumnElement: (columnId) => { var _a; return (_a = headerCellElementsRef.current.get(columnId)) !== null && _a !== void 0 ? _a : null; }
    }));
    return {
        dragDropState,
        headerRowRef,
        onColumnRef,
        onColumnClick,
        onColumnDragStart,
        onColumnDragMove,
        onColumnDragEnd,
        onColumnResizeStart: onColumnResize,
        onColumnResizeMove: onColumnResize,
        onColumnResizeEnd
    };
}
